import { AI } from "@/lib/ai/ai"
import { useEditor } from "../useEditor"
import { AuxiliaryAIRawLoader, RawLoader } from "@/lib/raw/raw-loader"
import { Culler } from "@/lib/culling/culler"
import { getLensfunTotalBytesLength } from "@/core/utils/lens/lens-profile"
import { CatalogManager } from "@/lib/catalog"
import { isPerformanceDebugEnabled } from "./feature-flag"
import { store } from "@/store/store"
import { previewsAtom } from "@/store/previews/previews-state"
import { facePreviewsAtom } from "@/store/importing/culling-state"
import { JpegEncoder } from "@/lib/jpeg/jpeg-encoder"

export type MemoryCounterCategory = "cpu" | "gpu" | "ai-thread" | "renderer" | "main-thread"

interface MemoryCounter {
	categories: MemoryCounterCategory[]
	handler: () => Promise<number>
}

class MemoryCounters {
	public get allCounters() {
		return this.counters.entries()
	}

	private counters = new Map<string, MemoryCounter>()

	public registerAllCounters() {
		/* Renderers */

		SharedMemoryCounters.registerCounter(
			"AI Renderer",
			["gpu", "renderer", "ai-thread"],
			async () => {
				return await AI.getRendererTotalBytesUsage()
			},
		)

		SharedMemoryCounters.registerCounter(
			"Main Renderer",
			["gpu", "renderer", "main-thread"],
			async () => {
				const editor = useEditor()
				return Promise.resolve(
					editor.renderer.backend.textureMemoryCounters.totalMaxAllocatedBytesUsage,
				)
			},
		)

		/* WASM */

		SharedMemoryCounters.registerCounter(
			"Main Raw Loader",
			["cpu", "main-thread"],
			async () => {
				return await RawLoader.getTotalBytesUsage()
			},
		)

		SharedMemoryCounters.registerCounter("AI Raw Loader", ["cpu", "ai-thread"], async () => {
			return await AuxiliaryAIRawLoader.getTotalBytesUsage()
		})

		SharedMemoryCounters.registerCounter("Lensfun", ["cpu", "main-thread"], () => {
			return Promise.resolve(getLensfunTotalBytesLength())
		})

		SharedMemoryCounters.registerCounter("Catalog", ["cpu"], async () => {
			return await CatalogManager.getTotalBytesUsage()
		})

		/* Metadata */

		SharedMemoryCounters.registerCounter("Metadata Extractor", ["cpu"], async () => {
			return await JpegEncoder.getTotalBytesUsage()
		})

		/* Previews */

		SharedMemoryCounters.registerCounter("Previews", ["cpu"], async () => {
			const previews = store.get(previewsAtom)
			const facePreviews = store.get(facePreviewsAtom)
			const size = Object.values(previews)
				.concat(...Object.values(facePreviews))
				.reduce((acc, curr) => acc + curr.storageSize, 0)
			return Promise.resolve(size)
		})

		/* AI models */

		SharedMemoryCounters.registerCounter("AI Main Models", ["gpu", "ai-thread"], async () => {
			const detection = await AI.getDetection()
			return await detection.getTotalBytesUsage()
		})

		SharedMemoryCounters.registerCounter(
			"AI Culling Models",
			["gpu", "ai-thread"],
			async () => {
				return await Culler.getTotalBytesUsage()
			},
		)

		/* JS heap */

		if (performance["memory"] && performance["memory"]["totalJSHeapSize"]) {
			SharedMemoryCounters.registerCounter("JS Heap", ["cpu", "main-thread"], async () => {
				const value = performance["memory"]["totalJSHeapSize"] as number
				return Promise.resolve(value)
			})
		}
	}

	public registerCounter(
		label: string,
		categories: MemoryCounterCategory[],
		handler: () => Promise<number>,
	) {
		// Always register counters regardless of debug flag state
		// This allows dynamic enabling/disabling of performance debug mode
		this.counters.set(label, {
			categories,
			handler,
		})
	}

	public async getTotalBytesUsageInCategories(categories: MemoryCounterCategory[]) {
		const usage = await this.getUsageInCategories(categories)
		let totalBytes = 0

		for (const bytes of Object.values(usage)) {
			totalBytes += bytes
		}

		return totalBytes
	}

	public async getUsageInCategories(categories: MemoryCounterCategory[]) {
		// Only collect usage data when performance debug is enabled
		if (!isPerformanceDebugEnabled()) {
			return {}
		}

		const usages: Record<string, number> = {}

		for await (const [key, value] of this.counters.entries()) {
			if (categories.every((item) => value.categories.includes(item)) == false) {
				continue
			}

			usages[key] = await value.handler()
		}

		return usages
	}

	public async printAll() {
		if (!isPerformanceDebugEnabled()) {
			return
		}

		const records = {}

		let totalSize = 0

		for await (const [key, value] of this.counters.entries()) {
			const label = key
			const size = Math.fround((await value.handler()) / 1024 / 1024)
			const categories = value.categories.join(", ")

			records[label] = {
				"Size (MB)": size,
				Categories: categories,
			}

			totalSize += size
		}

		console.table(records)

		console.table({
			Total: {
				totalSize,
				Categories: "",
			},
		})
	}
}

export const SharedMemoryCounters = new MemoryCounters()

if (!import.meta.env.PROD && typeof window !== "undefined") {
	window["debugSharedMemoryCounters"] = SharedMemoryCounters
}
